import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import { State } from "./types";
import useStyles from "./styles";
import { UserContext } from "../../../UserProvider";
import { hasRole } from "../../../models/User";
import { permissions } from "../../../constants";
import { AlertTitle } from "@material-ui/lab";
import Alert from "@material-ui/lab/Alert";
import Progress from "../../../components/information/progress";
import {
  StoreOrderItem,
  StoreOrderState as OrderStateType
} from "../../../models/Store";
// import { OrderItemsTable } from "./components/OrderItemsTable";
import * as StoreOrderApi from "../../../api/storeOrderApi";
import { isOk } from "../../../result";
import {
  Button,
  Card,
  CardContent,
  Fab,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography
} from "@material-ui/core";
import moment from "moment";
import SaveIcon from "@material-ui/icons/Save";
import { StoreOrderState } from "../components/StoreOrderState";
import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import { useParams } from "react-router-dom";
import { useI18n } from "../../../hooks/useI18n";

export const StoreOrderOverview = () => {
  const params = useParams();
  const classes = useStyles();
  const [state, setState] = useState<State>({ type: "INITIAL" });
  const currentUser = useContext(UserContext);
  const { t } = useI18n();

  useEffect(() => {
    switch (state.type) {
      case "INITIAL":
        if (hasRole(currentUser, permissions.stores)) {
          if (params.storeOrderId && params.storeOrderId !== "any") {
            return setState({
              type: "LOADING",
              storeOrderId: params.storeOrderId
            });
          } else {
            return setState({
              type: "UNEXPECTED_ERROR",
              problem: {
                status: 400,
                title: "InvalidInput",
                detail:
                  "Missing or invalid params in the route: {storeId} and/or {storeOrderId}",
                traceId: "",
                errors: []
              }
            });
          }
        }
        return setState({ type: "NOT_ALLOWED" });

      case "LOADING":
        StoreOrderApi.getOrder(state.storeOrderId).then(result => {
          if (isOk(result)) {
            return setState({
              type: "VIEW",
              order: result.value,
              updateModel: {
                storeOrderId: result.value.id,
                state: result.value.state,
                comment: result.value.comment,
                addItems: [],
                deleteItems: [],
                updateItems: []
              }
            });
          }
          return setState({
            type: "UNEXPECTED_ERROR",
            problem: result.details
          });
        });
        break;

      case "SAVING":
        StoreOrderApi.updateOrder(
          state.updateModel.storeOrderId,
          state.updateModel.state
        ).then(result => {
          if (isOk(result)) {
            return setState({
              type: "VIEW",
              order: result.value,
              updateModel: {
                storeOrderId: result.value.id,
                state: result.value.state,
                comment: result.value.comment,
                addItems: [],
                deleteItems: [],
                updateItems: []
              }
            });
          }
          return setState({
            type: "UNEXPECTED_ERROR",
            problem: result.details
          });
        });
        break;

      case "REGENERATING":
        StoreOrderApi.regenerateOrder(state.storeOrderId).then(result => {
          if (isOk(result)) {
            return setState({
              type: "LOADING",
              storeOrderId: state.storeOrderId
            });
          }

          return setState({
            type: "UNEXPECTED_ERROR",
            problem: result.details
          });
        });
        break;
    }
  }, [state.type]);

  switch (state.type) {
    case "NOT_ALLOWED":
      return (
        <Alert severity="warning">
          {t("common.messages.notAllowedToSeeThisView")}
        </Alert>
      );

    case "UNEXPECTED_ERROR":
      return (
        <>
          <Alert severity="error">
            <AlertTitle>
              {state.problem.status}, {state.problem.title}
            </AlertTitle>
            {state.problem.detail} <strong>{state.problem.traceId}</strong>
          </Alert>
          <div className={classes.loading} style={{ textAlign: "center" }}>
            <Button
              color={"primary"}
              variant={"contained"}
              onClick={() => setState({ type: "INITIAL" })}
            >
              Refresh
            </Button>
          </div>
        </>
      );

    case "INITIAL":
    case "LOADING":
    case "REGENERATING":
    case "SAVING":
      return (
        <div className={classes.loading}>
          <Progress isModule={true} />
        </div>
      );

    case "VIEW":
    case "SHOW_SAVE_DIALOG":
      const handleAdd = (orderItem: StoreOrderItem) => {
        let updateModel = state.updateModel;
        const orgItem = state.order.items.find(
          i => i.product.id == orderItem.product.id
        );

        if (
          orgItem &&
          updateModel.deleteItems.find(item => item.id === orgItem.id)
        ) {
          // if the item has been flagged as deleted already

          // remove from deleted
          updateModel.deleteItems = updateModel.deleteItems.filter(
            i => i.id !== orgItem.id
          );

          // and add as updated
          updateModel.updateItems = [
            ...updateModel.updateItems,
            { id: orgItem.id, quantity: orderItem.quantity }
          ];
        } else if (
          !updateModel.addItems.find(
            item => item.productId === orderItem.product.id
          )
        ) {
          // if the item has not yet been flagged as added

          // then add it
          updateModel.addItems = [
            ...updateModel.addItems,
            {
              productId: orderItem.product.id,
              quantity: orderItem.quantity,
              quantityUnit: orderItem.unit
            }
          ];
        }

        setState({
          ...state,
          updateModel: updateModel
        });
      };

      const handleEdit = (orderItem: StoreOrderItem) => {
        let updateModel = state.updateModel;

        if (
          updateModel.addItems.find(
            item => item.productId === orderItem.product.id
          )
        ) {
          // if it has been flagged as added already, just modify that entry
          updateModel.addItems = updateModel.addItems.map(item =>
            item.productId === orderItem.product.id
              ? {
                  productId: orderItem.product.id,
                  quantity: orderItem.quantity,
                  quantityUnit: orderItem.unit
                }
              : item
          );
        } else if (
          !updateModel.updateItems.find(item => item.id === orderItem.id)
        ) {
          // if it is not flagged as updated yet, do that..
          updateModel.updateItems = [
            ...updateModel.updateItems,
            { id: orderItem.id, quantity: orderItem.quantity }
          ];
        } else {
          // otherwise just modify the existing entry
          updateModel.updateItems = updateModel.updateItems.map(item =>
            item.id === orderItem.id
              ? { id: orderItem.id, quantity: orderItem.quantity }
              : item
          );
        }

        setState({
          ...state,
          updateModel: updateModel
        });
      };

      const handleDelete = (orderItem: StoreOrderItem) => {
        let updateModel = state.updateModel;

        if (
          updateModel.addItems.find(
            item => item.productId === orderItem.product.id
          )
        ) {
          // if the item has been flagged as added

          // then remove that
          updateModel.addItems = updateModel.addItems.filter(
            i => i.productId !== orderItem.product.id
          );
        } else if (
          !updateModel.deleteItems.find(item => item.id === orderItem.id)
        ) {
          // if it hasn't already been flagged as deleted

          // just make sure it isn't also flagged as updated
          updateModel.updateItems = updateModel.updateItems.filter(
            i => i.id !== orderItem.id
          );

          // flag as deleted
          updateModel.deleteItems = [
            ...updateModel.deleteItems,
            { id: orderItem.id }
          ];
        }

        setState({
          ...state,
          updateModel: updateModel
        });
      };

      const handleRegenerate = () => {
        setState({
          type: "REGENERATING",
          storeOrderId: state.order.id
        });
      };

      const handleShowSaveDialog = () => {
        setState({ ...state, type: "SHOW_SAVE_DIALOG" });
      };

      const handleCancelSaveDialog = () => {
        setState({ ...state, type: "VIEW" });
      };

      const handleConfirmSaveDialog = (): boolean => {
        let newState =
          state.order.state === OrderStateType.Confirmed
            ? OrderStateType.Ordered
            : OrderStateType.Confirmed;
        setState({
          type: "SAVING",
          updateModel: { ...state.updateModel, state: newState }
        });
        return true;
      };

      const renderDialogTitle = (): string => {
        switch (state.order.state) {
          case OrderStateType.Proposed:
            return "Confirm order proposal";
          case OrderStateType.Confirmed:
            return t("common.actions.saveOrder");
        }

        return "";
      };

      const renderDialogConfirmText = (): string => {
        switch (state.order.state) {
          case OrderStateType.Proposed:
            return "Confirm";
          case OrderStateType.Confirmed:
            return "Save";
        }

        return "";
      };

      return (
        <>
          <Card className={classes.info}>
            <CardContent>
              <StoreOrderState
                style={{ marginBottom: "2rem" }}
                state={state.order.state}
              />
              <Typography gutterBottom variant="h6" component="h6">
                {state.order.distributors.map(d => d.name).join(", ")}
              </Typography>
              <List>
                <ListItem key={"store"} className={classes.listItemTight}>
                  <ListItemText
                    primary={"Store(s)"}
                    secondary={state.order.stores.map(d => d.name).join(", ")}
                  />
                </ListItem>
                <ListItem key={"comment"} className={classes.listItemTight}>
                  <ListItemText
                    primary={"Comment(s)"}
                    secondary={
                      state.order.comment ? (
                        <TextField
                          style={{ width: "100%" }}
                          multiline
                          aria-readonly
                          disabled
                          value={state.order.comment}
                        />
                      ) : (
                        "..."
                      )
                    }
                  />
                </ListItem>
                <ListItem
                  key={"confirmDeadline"}
                  className={classes.listItemTight}
                >
                  <ListItemText
                    primary={t("common.messages.confirmDeadline")}
                    secondary={moment(state.order.confirmDeadline).format(
                      "YYYY-MM-DD HH:mm"
                    )}
                  />
                </ListItem>
                <ListItem
                  key={"orderDeadline"}
                  className={classes.listItemTight}
                >
                  <ListItemText
                    primary={t("common.messages.orderDeadline")}
                    secondary={moment(state.order.orderDeadline).format(
                      "YYYY-MM-DD HH:mm"
                    )}
                  />
                </ListItem>
                <ListItem
                  key={"estimatedDelivery"}
                  className={classes.listItemTight}
                >
                  <ListItemText
                    primary={t("common.messages.estimatedDelivery")}
                    secondary={moment(state.order.estimatedDelivery).format(
                      "YYYY-MM-DD HH:mm"
                    )}
                  />
                </ListItem>
              </List>
              {state.order.orderNotes ? (
                <Alert severity="warning">{state.order.orderNotes}</Alert>
              ) : null}
            </CardContent>
          </Card>
          <div style={{ marginBottom: "4rem" }}>
            {/* <OrderItemsTable
              order={state.order}
              onAdd={handleAdd}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onRegenerate={handleRegenerate}
            /> */}
          </div>
          {state.order.state === OrderStateType.Proposed ||
          state.order.state === OrderStateType.Confirmed ? (
            <>
              <div className={classes.buttons}>
                <Fab
                  color="primary"
                  aria-label="save"
                  onClick={handleShowSaveDialog}
                >
                  <SaveIcon />
                </Fab>
              </div>
              <ConfirmDialog
                visible={state.type === "SHOW_SAVE_DIALOG"}
                titleText={renderDialogTitle()}
                confirmText={renderDialogConfirmText()}
                onCancel={handleCancelSaveDialog}
                onConfirm={handleConfirmSaveDialog}
              >
                <div>
                  {state.order.state === OrderStateType.Proposed ? (
                    <>
                      <p>
                        You are now confirming{" "}
                        <b>
                          {state.order.items.length +
                            (state.updateModel.addItems
                              ? state.updateModel.addItems.length
                              : 0)}
                        </b>{" "}
                        items.
                        {state.updateModel.addItems &&
                          state.updateModel.addItems.length > 0 && (
                            <>
                              <b>{state.updateModel.addItems.length}</b> of
                              these items have been added.{" "}
                            </>
                          )}
                        {state.updateModel.updateItems &&
                          state.updateModel.updateItems.length > 0 && (
                            <>
                              <b>{state.updateModel.updateItems.length}</b> of
                              these items have been updated.{" "}
                            </>
                          )}
                        {state.updateModel.deleteItems &&
                          state.updateModel.deleteItems.length > 0 && (
                            <>
                              <b>{state.updateModel.deleteItems.length}</b> of
                              these items have been deleted.{" "}
                            </>
                          )}
                      </p>
                      <p>
                        {t("common.messages.pressConfirmToContinue")}
                        there are more changes to be done.
                      </p>
                    </>
                  ) : null}
                  {state.order.state === OrderStateType.Confirmed ? (
                    <>
                      <p>
                        When saving the order it will be placed in a state of{" "}
                        {t("common.messages.orderedAndCanLaterBePickedUp")}
                        delivery.
                      </p>
                      <p>
                        {t("common.messages.pressSaveToContinue")}
                        order is not ready.
                      </p>
                    </>
                  ) : null}
                  <TextField
                    className={classes.field}
                    label="Comment"
                    onChange={e => {
                      return setState({
                        ...state,
                        updateModel: {
                          ...state.updateModel,
                          comment: e.target.value
                        }
                      });
                    }}
                  />
                </div>
              </ConfirmDialog>
            </>
          ) : null}
        </>
      );
  }
};
